import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Eye, Upload, FileText, X, Search,
  MapPin, DollarSign, Calendar, User, Building2, CheckCircle, Clock, AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// 16 African Regions
const REGIONS = [
  { name: "Southern Apex Region", code: "SAR", countries: ["South Africa", "Lesotho", "Eswatini"] },
  { name: "Southern Interior Resource Belt", code: "SIRB", countries: ["Botswana", "Zimbabwe", "Zambia"] },
  { name: "Southwest Atlantic Corridor", code: "SWAC", countries: ["Namibia", "Angola"] },
  { name: "Mozambique Channel Growth Region", code: "MCGR", countries: ["Mozambique", "Malawi", "Madagascar"] },
  { name: "Indian Ocean Islands Bloc", code: "IOIB", countries: ["Mauritius", "Seychelles", "Comoros"] },
  { name: "Great Lakes Central Core", code: "GLCC", countries: ["Democratic Republic of the Congo", "Republic of the Congo", "Central African Republic"] },
  { name: "Great Lakes East", code: "GLE", countries: ["Rwanda", "Burundi", "Uganda"] },
  { name: "East African Coastal & Trade Belt", code: "EACTB", countries: ["Kenya", "Tanzania"] },
  { name: "Horn of Africa Corridor", code: "HOAC", countries: ["Ethiopia", "Eritrea", "Djibouti", "Somalia"] },
  { name: "Nile Basin Alliance", code: "NBA", countries: ["Egypt", "Sudan", "South Sudan"] },
  { name: "Central West Economic Cluster", code: "CWEC", countries: ["Cameroon", "Equatorial Guinea", "Gabon", "São Tomé and Príncipe"] },
  { name: "West African Atlantic Core", code: "WAAC", countries: ["Nigeria", "Ghana", "Benin", "Togo"] },
  { name: "Upper Guinea Coast", code: "UGC", countries: ["Senegal", "The Gambia", "Guinea", "Guinea-Bissau", "Sierra Leone", "Liberia"] },
  { name: "Sahel Development Belt", code: "SDB", countries: ["Mali", "Burkina Faso", "Niger", "Chad"] },
  { name: "Maghreb North Atlantic", code: "MNA", countries: ["Morocco", "Algeria", "Tunisia", "Libya"] },
  { name: "Lusophone & Atlantic Islands", code: "LAI", countries: ["Cabo Verde", "Côte d'Ivoire"] },
];

const INDUSTRIES = [
  "Renewable Energy", "Oil & Gas", "Mining", "Information Technology", "Smart Infrastructure",
  "Transportation", "Maritime & Ports", "Water & Sanitation", "Healthcare", "Medical Infrastructure",
  "Agriculture", "Agribusiness", "Manufacturing", "Industrial Development", "Real Estate",
  "Commercial Development", "Telecommunications", "Financial Services", "Education", "Tourism"
];

const SECTORS = [
  "Energy", "Technology", "Infrastructure", "Utilities", "Healthcare",
  "Agriculture", "Manufacturing", "Real Estate", "Finance", "Education"
];

const ProjectManagement = () => {
  const [projects, setProjects] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRegion, setFilterRegion] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    project_code: "",
    description: "",
    region: "",
    country: "",
    province: "",
    lat: "",
    lng: "",
    sector: "",
    industry: "",
    budget_total: "",
    project_manager: "",
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Seed projects and regions first
      await axios.post(`${API}/seed-projects`, {}, { headers });
      await axios.post(`${API}/seed-regions`, {}, { headers });
      
      const [projectsRes, regionsRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers }),
        axios.get(`${API}/regions`, { headers })
      ]);
      
      setProjects(projectsRes.data.projects || []);
      setRegions(regionsRes.data.regions || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleRegionChange = (regionName) => {
    setFormData(prev => ({ ...prev, region: regionName, country: "" }));
    const region = REGIONS.find(r => r.name === regionName);
    setSelectedCountries(region ? region.countries : []);
  };

  const handleCreateProject = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...formData,
        lat: formData.lat ? parseFloat(formData.lat) : null,
        lng: formData.lng ? parseFloat(formData.lng) : null,
        budget_total: parseFloat(formData.budget_total) || 0,
      };
      
      await axios.post(`${API}/projects/create`, payload, { headers });
      toast.success("Project created successfully");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error("Failed to create project");
    }
  };

  const handleUpdateProject = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...formData,
        lat: formData.lat ? parseFloat(formData.lat) : null,
        lng: formData.lng ? parseFloat(formData.lng) : null,
        budget_total: formData.budget_total ? parseFloat(formData.budget_total) : null,
      };
      
      await axios.put(`${API}/projects/${selectedProject.id}`, payload, { headers });
      toast.success("Project updated successfully");
      setShowEditDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("Failed to update project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm("Are you sure you want to delete this project?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/projects/${projectId}`, { headers });
      toast.success("Project deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  const openEditDialog = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name || "",
      project_code: project.project_code || "",
      description: project.description || "",
      region: project.region || "",
      country: project.country || "",
      province: project.province || "",
      lat: project.lat?.toString() || "",
      lng: project.lng?.toString() || "",
      sector: project.sector || "",
      industry: project.industry || "",
      budget_total: project.budget_total?.toString() || "",
      project_manager: project.project_manager || "",
      start_date: project.start_date || "",
      end_date: project.end_date || "",
    });
    const region = REGIONS.find(r => r.name === project.region);
    setSelectedCountries(region ? region.countries : []);
    setShowEditDialog(true);
  };

  const openViewDialog = (project) => {
    setSelectedProject(project);
    setShowViewDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: "", project_code: "", description: "", region: "", country: "",
      province: "", lat: "", lng: "", sector: "", industry: "",
      budget_total: "", project_manager: "", start_date: "", end_date: "",
    });
    setSelectedCountries([]);
    setSelectedProject(null);
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.project_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.country?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = filterRegion === "all" || p.region === filterRegion;
    const matchesStatus = filterStatus === "all" || p.status === filterStatus;
    return matchesSearch && matchesRegion && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const colors = {
      active: "bg-green-500/20 text-green-400 border-green-500/30",
      planning: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      completed: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      on_hold: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    };
    return colors[status] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
  };

  const getApprovalBadge = (status) => {
    const colors = {
      approved: "bg-green-500/20 text-green-400 border-green-500/30",
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      rejected: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return colors[status] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
  };

  const formatCurrency = (val) => {
    if (!val) return "$0";
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    return `$${val.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Project Management
          </h1>
          <p className="text-slate-400 mt-1">Create, edit, and manage projects across Africa</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-[#c9a227] hover:bg-[#b8922a] text-[#0b1c2d] font-semibold"
          data-testid="create-project-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create New Project
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Projects</p>
                <p className="text-xl font-bold text-white">{projects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Active</p>
                <p className="text-xl font-bold text-white">{projects.filter(p => p.status === "active").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Pending Approval</p>
                <p className="text-xl font-bold text-white">{projects.filter(p => p.approval_status === "pending").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[#c9a227]/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#c9a227]" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Budget</p>
                <p className="text-xl font-bold text-[#c9a227]">
                  {formatCurrency(projects.reduce((acc, p) => acc + (p.budget_total || 0), 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-900/50 border-slate-700 text-white"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={filterRegion} onValueChange={setFilterRegion}>
              <SelectTrigger className="w-full md:w-[220px] bg-slate-900/50 border-slate-700 text-white">
                <SelectValue placeholder="Filter by Region" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all">All Regions</SelectItem>
                {REGIONS.map(r => (
                  <SelectItem key={r.code} value={r.name}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[180px] bg-slate-900/50 border-slate-700 text-white">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="planning">Planning</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-white">Projects ({filteredProjects.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Project</th>
                  <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Region / Country</th>
                  <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Industry</th>
                  <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Budget</th>
                  <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Approval</th>
                  <th className="text-right py-3 px-4 text-sm text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map(project => (
                  <tr key={project.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                    <td className="py-4 px-4">
                      <div>
                        <p className="text-white font-medium">{project.name}</p>
                        <p className="text-xs text-slate-500">{project.project_code}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="text-slate-300 text-sm">{project.country}</p>
                        <p className="text-xs text-slate-500">{project.region}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-slate-300 text-sm">{project.industry || project.sector}</p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-[#c9a227] font-medium">{formatCurrency(project.budget_total)}</p>
                    </td>
                    <td className="py-4 px-4">
                      <Badge className={`${getStatusBadge(project.status)} border`}>
                        {project.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-4">
                      <Badge className={`${getApprovalBadge(project.approval_status)} border`}>
                        {project.approval_status || "pending"}
                      </Badge>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openViewDialog(project)}
                          className="text-slate-400 hover:text-white"
                          data-testid={`view-${project.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(project)}
                          className="text-slate-400 hover:text-[#c9a227]"
                          data-testid={`edit-${project.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteProject(project.id)}
                          className="text-slate-400 hover:text-red-400"
                          data-testid={`delete-${project.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredProjects.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                No projects found matching your criteria
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#c9a227]">Create New Project</DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter all project details for approval submission
            </DialogDescription>
          </DialogHeader>
          <ProjectForm
            formData={formData}
            setFormData={setFormData}
            selectedCountries={selectedCountries}
            handleRegionChange={handleRegionChange}
          />
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleCreateProject} className="bg-[#c9a227] hover:bg-[#b8922a] text-[#0b1c2d]">
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#c9a227]">Edit Project</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update project information
            </DialogDescription>
          </DialogHeader>
          <ProjectForm
            formData={formData}
            setFormData={setFormData}
            selectedCountries={selectedCountries}
            handleRegionChange={handleRegionChange}
            isEdit
          />
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleUpdateProject} className="bg-[#c9a227] hover:bg-[#b8922a] text-[#0b1c2d]">
              Update Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Project Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#c9a227]">{selectedProject?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedProject?.project_code}
            </DialogDescription>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-400">Region</p>
                    <p className="text-white">{selectedProject.region}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Country / Province</p>
                    <p className="text-white">{selectedProject.country} {selectedProject.province && `/ ${selectedProject.province}`}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">GPS Coordinates</p>
                    <p className="text-white">{selectedProject.lat}, {selectedProject.lng}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Sector / Industry</p>
                    <p className="text-white">{selectedProject.sector} / {selectedProject.industry}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-400">Project Manager</p>
                    <p className="text-white">{selectedProject.project_manager}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Budget</p>
                    <p className="text-[#c9a227] font-bold">{formatCurrency(selectedProject.budget_total)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Status / Approval</p>
                    <div className="flex gap-2 mt-1">
                      <Badge className={`${getStatusBadge(selectedProject.status)} border`}>{selectedProject.status}</Badge>
                      <Badge className={`${getApprovalBadge(selectedProject.approval_status)} border`}>{selectedProject.approval_status}</Badge>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Timeline</p>
                    <p className="text-white">{selectedProject.start_date} - {selectedProject.end_date}</p>
                  </div>
                </div>
              </div>
              {selectedProject.description && (
                <div>
                  <p className="text-sm text-slate-400">Description</p>
                  <p className="text-slate-300 mt-1">{selectedProject.description}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-400 mb-2">Completion Progress</p>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#c9a227] transition-all"
                    style={{ width: `${selectedProject.completion_percentage || 0}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">{selectedProject.completion_percentage || 0}% complete</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Project Form Component
const ProjectForm = ({ formData, setFormData, selectedCountries, handleRegionChange, isEdit }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Project Name */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Project Name *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter project name"
          className="bg-slate-800 border-slate-700 text-white"
          data-testid="project-name-input"
        />
      </div>

      {/* Project Code */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Project Code</label>
        <Input
          value={formData.project_code}
          onChange={(e) => setFormData(prev => ({ ...prev, project_code: e.target.value }))}
          placeholder="e.g., PRJ001"
          className="bg-slate-800 border-slate-700 text-white"
        />
      </div>

      {/* Region */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Region *</label>
        <Select value={formData.region} onValueChange={handleRegionChange}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Select region" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px]">
            {REGIONS.map(r => (
              <SelectItem key={r.code} value={r.name}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Country */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Country *</label>
        <Select
          value={formData.country}
          onValueChange={(val) => setFormData(prev => ({ ...prev, country: val }))}
          disabled={!formData.region}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            {selectedCountries.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Province */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Province / State</label>
        <Input
          value={formData.province}
          onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
          placeholder="Enter province"
          className="bg-slate-800 border-slate-700 text-white"
        />
      </div>

      {/* Project Manager */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Project Manager *</label>
        <Input
          value={formData.project_manager}
          onChange={(e) => setFormData(prev => ({ ...prev, project_manager: e.target.value }))}
          placeholder="Enter project manager name"
          className="bg-slate-800 border-slate-700 text-white"
        />
      </div>

      {/* GPS Coordinates */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Latitude</label>
        <Input
          type="number"
          step="any"
          value={formData.lat}
          onChange={(e) => setFormData(prev => ({ ...prev, lat: e.target.value }))}
          placeholder="e.g., -26.2041"
          className="bg-slate-800 border-slate-700 text-white"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm text-slate-400">Longitude</label>
        <Input
          type="number"
          step="any"
          value={formData.lng}
          onChange={(e) => setFormData(prev => ({ ...prev, lng: e.target.value }))}
          placeholder="e.g., 28.0473"
          className="bg-slate-800 border-slate-700 text-white"
        />
      </div>

      {/* Sector */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Sector *</label>
        <Select
          value={formData.sector}
          onValueChange={(val) => setFormData(prev => ({ ...prev, sector: val }))}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Select sector" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            {SECTORS.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Industry */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Industry</label>
        <Select
          value={formData.industry}
          onValueChange={(val) => setFormData(prev => ({ ...prev, industry: val }))}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Select industry" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px]">
            {INDUSTRIES.map(i => (
              <SelectItem key={i} value={i}>{i}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Budget */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Budget Total (USD) *</label>
        <Input
          type="number"
          value={formData.budget_total}
          onChange={(e) => setFormData(prev => ({ ...prev, budget_total: e.target.value }))}
          placeholder="e.g., 50000000"
          className="bg-slate-800 border-slate-700 text-white"
        />
      </div>

      {/* Start Date */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">Start Date</label>
        <Input
          type="date"
          value={formData.start_date}
          onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
          className="bg-slate-800 border-slate-700 text-white"
        />
      </div>

      {/* End Date */}
      <div className="space-y-2">
        <label className="text-sm text-slate-400">End Date</label>
        <Input
          type="date"
          value={formData.end_date}
          onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
          className="bg-slate-800 border-slate-700 text-white"
        />
      </div>

      {/* Description - Full width */}
      <div className="space-y-2 md:col-span-2">
        <label className="text-sm text-slate-400">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Enter project description"
          rows={3}
          className="w-full bg-slate-800 border border-slate-700 text-white rounded-md p-3 focus:border-[#c9a227] focus:ring-1 focus:ring-[#c9a227] outline-none"
        />
      </div>
    </div>
  );
};

export default ProjectManagement;
